import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import {
  type CreatePaymentProviderRequest,
  isPaymentProviderValue,
  type ListPaymentProvidersResponse,
  PAYMENT_PROVIDER_VALUES,
  type PaymentProviderConfigRecord,
  type PaymentProviderValue,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useCreatePaymentProvider,
  usePaymentProviders,
  useRevealPaymentProviderKey,
} from '../api/payment-providers.ts';
import { Modal } from '../components/Modal.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS_MONO_ON_HIGHEST, INPUT_CLASS_ON_HIGHEST } from '../styles/forms.ts';

/**
 * `/payment-providers` — payment aggregator credential CRUD (PR P16).
 *
 * This page is the tenant-free counterpart to `/pricing` — it manages
 * the `payment_provider_configs` rows that `createZPayProvider` reads
 * at signing time. Every topup in the system depends on this surface;
 * a misconfigured row 503s `/v1/billing/topup` until ops fixes it.
 *
 * Layout — DESIGN.md §5+§6 + the `.stitch/designs/payment-providers.html`
 * reference:
 *
 *   1. Page header — title + subtitle + "New Provider" button.
 *   2. Info banner — "changes take effect within 60 seconds" (matches
 *      the isolate-local cache TTL in `db/payment-provider-configs.ts`).
 *   3. Provider list — rows grouped active-first. Empty-state card when
 *      the table is empty (fresh deploy, pre-seed).
 *   4. Reveal interaction — inline `[Reveal key]` button replaces the
 *      masked key with the plaintext one for the single row that was
 *      clicked. Every click hits the audited `/reveal-key` endpoint.
 *   5. Create / rotate modal — single form that writes a new row. The
 *      `Make active` toggle triggers the retire-flip on the prior
 *      active row atomically (platform-api batch).
 *   6. Type-to-confirm — whenever `Make active` is set AND a prior
 *      active row exists, the submit opens a second modal requiring
 *      the operator to type the provider name exactly. Matches the
 *      pricing + balance-adjust pattern.
 *
 * Append-only rotation — there is intentionally NO "edit in place"
 * button on existing rows. Rotating a provider means creating a new
 * row with new credentials and `make_active=true`; the DB retires the
 * prior active row in the same batch. The retired row's plaintext key
 * remains readable forever via reveal so auditors can verify historical
 * signatures.
 *
 * Plaintext-by-design — secret keys are stored plaintext in D1 (see
 * CLAUDE.md "Do NOT" #5 + migration 0016 rationale). The list view
 * only ever renders the `secret_key_masked` string; plaintext comes
 * from the `reveal-key` endpoint which writes an `ops_audit` row on
 * every call.
 */

export function PaymentProvidersPage(): JSX.Element {
  const t = useT();
  const list = usePaymentProviders();

  if (list.isPending) {
    return (
      <section
        className="flex flex-col gap-8"
        data-testid="payment-providers-page-loading"
      >
        <div className="h-24 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <div className="h-16 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <div className="h-64 animate-pulse rounded-xl bg-surface-container" aria-hidden />
        <span className="sr-only">{t('payment_providers.loading_aria')}</span>
      </section>
    );
  }

  if (list.isError) {
    return (
      <section
        className="flex flex-col gap-4 rounded-xl bg-error-container p-6 text-on-error-container"
        role="alert"
        data-testid="payment-providers-page-error"
      >
        <h2 className="text-base font-semibold">
          {t('payment_providers.error.title')}
        </h2>
        <p className="text-sm">
          {list.error instanceof OpsApiError
            ? `${list.error.code}: ${list.error.message}`
            : t('common.network_error')}
        </p>
        <div>
          <button
            type="button"
            onClick={() => void list.refetch()}
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('common.retry')}
          </button>
        </div>
      </section>
    );
  }

  return <PaymentProvidersReady data={list.data} />;
}

interface ReadyProps {
  readonly data: ListPaymentProvidersResponse;
}

function PaymentProvidersReady({ data }: ReadyProps): JSX.Element {
  const t = useT();
  // `prefill` keeps the create-modal seeded with a specific provider
  // when the operator clicked "Rotate" on an existing row (vs the blank
  // "+ New Provider" button up top). When null → blank form.
  const [prefill, setPrefill] = useState<PaymentProviderValue | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);

  // Group rows by provider for display; each provider's active row
  // first, then retired rows newest-first. Server already sorts by
  // (provider ASC, updated_at DESC), we just need to hoist active.
  const rowsByProvider = useMemo(() => groupByProvider(data.providers), [data.providers]);

  function openCreateModal(provider: PaymentProviderValue | null): void {
    setPrefill(provider);
    setModalOpen(true);
  }

  function closeCreateModal(): void {
    setModalOpen(false);
    setPrefill(null);
  }

  return (
    <section className="flex flex-col gap-6" data-testid="payment-providers-page">
      <header className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
              {t('payment_providers.title')}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-on-surface-variant">
              {t('payment_providers.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => openCreateModal(null)}
            data-testid="payment-providers-new"
            className="inline-flex items-center gap-2 rounded-xl bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
              add
            </span>
            <span>{t('payment_providers.new')}</span>
          </button>
        </div>
      </header>

      {/* Info banner — spells out the 60s cache contract so an operator
          who just rotated doesn't panic when the change isn't instant. */}
      <article
        className="flex items-start gap-3 rounded-xl bg-surface-container p-4"
        data-testid="payment-providers-info"
      >
        <span
          aria-hidden="true"
          className="material-symbols-outlined mt-0.5 text-[18px] text-primary"
        >
          info
        </span>
        <p className="text-sm leading-relaxed text-on-surface-variant">
          {t('payment_providers.banner')}
        </p>
      </article>

      {data.providers.length === 0 ? (
        <EmptyState onCreate={() => openCreateModal(null)} />
      ) : (
        <div className="flex flex-col gap-6">
          {rowsByProvider.map(({ provider, rows }) => (
            <ProviderGroup
              key={provider}
              provider={provider}
              rows={rows}
              // DB `provider` column is free-form TEXT; guard before
              // handing it to the modal so an unknown provider string
              // (e.g. migration-ahead-of-enum) falls back to the blank-
              // provider path instead of corrupting the URL defaults.
              onRotate={() =>
                isPaymentProviderValue(provider)
                  ? openCreateModal(provider)
                  : openCreateModal(null)
              }
            />
          ))}
        </div>
      )}

      {isModalOpen ? (
        <CreateProviderModal
          data={data}
          prefillProvider={prefill}
          onClose={closeCreateModal}
        />
      ) : null}
    </section>
  );
}

// ─── Grouping helper ─────────────────────────────────────────────────────

interface ProviderGrouping {
  readonly provider: string;
  readonly rows: readonly PaymentProviderConfigRecord[];
}

function groupByProvider(
  records: readonly PaymentProviderConfigRecord[],
): readonly ProviderGrouping[] {
  const map = new Map<string, PaymentProviderConfigRecord[]>();
  for (const record of records) {
    const existing = map.get(record.provider);
    if (existing !== undefined) {
      existing.push(record);
    } else {
      map.set(record.provider, [record]);
    }
  }
  const out: ProviderGrouping[] = [];
  for (const [provider, rows] of map) {
    // Active first, then retired — the list response is already sorted
    // newest-first within that partition, so a stable partition suffices.
    const sorted = [...rows].sort((a, b) => {
      if (a.is_active === b.is_active) return 0;
      return a.is_active ? -1 : 1;
    });
    out.push({ provider, rows: sorted });
  }
  return out;
}

// ─── Provider group (one section per provider) ──────────────────────────

interface ProviderGroupProps {
  readonly provider: string;
  readonly rows: readonly PaymentProviderConfigRecord[];
  readonly onRotate: () => void;
}

function ProviderGroup({ provider, rows, onRotate }: ProviderGroupProps): JSX.Element {
  const t = useT();
  const hasActive = rows.some((r) => r.is_active);
  return (
    <article
      className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      data-testid={`payment-providers-group-${provider}`}
    >
      <header className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          <span
            aria-hidden="true"
            className="material-symbols-outlined text-[16px] text-primary"
          >
            credit_card
          </span>
          <span>{provider}</span>
        </h3>
        {hasActive ? (
          <button
            type="button"
            onClick={onRotate}
            data-testid={`payment-providers-rotate-${provider}`}
            className="inline-flex items-center gap-1 rounded-lg bg-surface-container-highest px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[14px]">
              swap_horiz
            </span>
            <span>{t('payment_providers.row.rotate')}</span>
          </button>
        ) : null}
      </header>
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <ProviderRow key={row.id} row={row} />
        ))}
      </div>
    </article>
  );
}

// ─── One row — masked key + reveal + status chip ─────────────────────────

interface ProviderRowProps {
  readonly row: PaymentProviderConfigRecord;
}

function ProviderRow({ row }: ProviderRowProps): JSX.Element {
  const t = useT();
  const reveal = useRevealPaymentProviderKey();
  const toast = useToast();
  const [revealed, setRevealed] = useState<string | null>(null);

  async function handleReveal(): Promise<void> {
    try {
      const { secret_key } = await reveal.mutateAsync(row.id);
      setRevealed(secret_key);
    } catch (err) {
      toast.error(
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err),
      );
    }
  }

  return (
    <div
      data-testid={`payment-providers-row-${row.id}`}
      data-is-active={row.is_active}
      className={[
        'grid grid-cols-[minmax(0,1fr)_180px_120px_160px_140px] items-center gap-4 rounded-lg px-4 py-3 transition-colors',
        row.is_active ? 'bg-surface-container-high' : 'bg-surface-container-low',
      ].join(' ')}
    >
      {/* Credentials (id + masked or revealed key) */}
      <div className="flex min-w-0 flex-col gap-1">
        <span
          className="truncate font-mono text-xs text-on-surface-variant"
          data-testid={`payment-providers-row-${row.id}-id`}
          title={row.id}
        >
          {row.id}
        </span>
        <span
          className="truncate font-mono text-sm text-on-surface"
          data-testid={`payment-providers-row-${row.id}-key`}
        >
          {revealed !== null ? revealed : row.secret_key_masked}
        </span>
      </div>

      {/* PID + submit URL — stacked so the wider column doesn't overflow on small screens */}
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
          {t('payment_providers.row.pid')}
        </span>
        <span
          className="truncate font-mono text-xs text-on-surface"
          data-testid={`payment-providers-row-${row.id}-pid`}
          title={row.pid}
        >
          {row.pid}
        </span>
      </div>

      {/* Status chip */}
      <StatusChip isActive={row.is_active} />

      {/* Updated metadata */}
      <div className="flex min-w-0 flex-col gap-1">
        <span
          className="truncate text-xs text-on-surface-variant"
          data-testid={`payment-providers-row-${row.id}-updated-by`}
          title={row.updated_by ?? ''}
        >
          {row.updated_by ?? '—'}
        </span>
        <span className="font-mono text-[11px] text-outline">
          {formatIso(row.updated_at)}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => void handleReveal()}
          disabled={reveal.isPending}
          data-testid={`payment-providers-row-${row.id}-reveal`}
          className="inline-flex items-center gap-1 rounded-lg bg-surface-container-highest px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-60"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[14px]">
            {revealed !== null ? 'check' : 'visibility'}
          </span>
          <span>
            {revealed !== null
              ? t('payment_providers.row.revealed')
              : reveal.isPending
                ? t('payment_providers.row.revealing')
                : t('payment_providers.row.reveal')}
          </span>
        </button>
      </div>
    </div>
  );
}

interface StatusChipProps {
  readonly isActive: boolean;
}

function StatusChip({ isActive }: StatusChipProps): JSX.Element {
  const t = useT();
  if (isActive) {
    return (
      <span
        data-testid="payment-providers-chip-active"
        className="inline-flex w-fit items-center gap-1.5 rounded-full bg-surface-container-highest px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-primary"
      >
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 rounded-full bg-tertiary animate-pulse-tertiary"
        />
        <span>{t('payment_providers.chip.active')}</span>
      </span>
    );
  }
  return (
    <span
      data-testid="payment-providers-chip-retired"
      className="inline-flex w-fit items-center rounded-full bg-surface-container-high px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
    >
      {t('payment_providers.chip.retired')}
    </span>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────

interface EmptyStateProps {
  readonly onCreate: () => void;
}

function EmptyState({ onCreate }: EmptyStateProps): JSX.Element {
  const t = useT();
  return (
    <article
      data-testid="payment-providers-empty"
      className="flex flex-col items-center gap-3 rounded-xl border border-outline-variant/15 border-dashed bg-surface-container px-6 py-16 text-center"
    >
      <span
        aria-hidden="true"
        className="material-symbols-outlined text-[48px] text-outline"
      >
        account_balance_wallet
      </span>
      <p className="text-base text-on-surface-variant">{t('payment_providers.empty.title')}</p>
      <p className="text-sm text-outline">{t('payment_providers.empty.body')}</p>
      <button
        type="button"
        onClick={onCreate}
        data-testid="payment-providers-empty-create"
        className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)]"
      >
        <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
          add
        </span>
        <span>{t('payment_providers.empty.cta')}</span>
      </button>
    </article>
  );
}

// ─── Create modal ────────────────────────────────────────────────────────

interface CreateProviderModalProps {
  readonly data: ListPaymentProvidersResponse;
  readonly prefillProvider: PaymentProviderValue | null;
  readonly onClose: () => void;
}

interface DraftRow {
  readonly provider: PaymentProviderValue;
  readonly pid: string;
  readonly secretKey: string;
  readonly submitUrl: string;
  readonly mapiUrl: string;
  readonly queryUrl: string;
  readonly note: string;
  readonly makeActive: boolean;
}

function CreateProviderModal({
  data,
  prefillProvider,
  onClose,
}: CreateProviderModalProps): JSX.Element {
  const t = useT();
  // Prefill is either a "Rotate" click on an existing active row (we
  // seed the URLs from it to save typing) or a blank "+ New Provider"
  // click from the top-right button (blank form, zpay-default URLs).
  const prefillRow = useMemo(() => {
    if (prefillProvider === null) return null;
    return (
      data.providers.find(
        (r) => r.provider === prefillProvider && r.is_active,
      ) ?? null
    );
  }, [data.providers, prefillProvider]);

  const [provider, setProvider] = useState<PaymentProviderValue>(
    prefillProvider ?? 'zpay',
  );
  const [pid, setPid] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [submitUrl, setSubmitUrl] = useState(
    prefillRow?.submit_url ?? defaultSubmitUrl(provider),
  );
  const [mapiUrl, setMapiUrl] = useState(
    prefillRow?.mapi_url ?? defaultMapiUrl(provider),
  );
  const [queryUrl, setQueryUrl] = useState(
    prefillRow?.query_url ?? defaultQueryUrl(provider),
  );
  const [note, setNote] = useState('');
  // Default to true because the common workflow is "seed first row" /
  // "rotate" — both want the new row active. Operator can uncheck for
  // a draft-style staging write.
  const [makeActive, setMakeActive] = useState(true);

  const [formError, setFormError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftRow | null>(null);

  const hasPriorActive = useMemo(
    () => data.providers.some((r) => r.provider === provider && r.is_active),
    [data.providers, provider],
  );

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const pidTrim = pid.trim();
    const submitTrim = submitUrl.trim();
    const mapiTrim = mapiUrl.trim();
    const queryTrim = queryUrl.trim();
    if (pidTrim.length === 0) {
      setFormError(t('payment_providers.create.error.pid_required'));
      return;
    }
    // secret_key is NOT trimmed on the server (some vendors issue keys
    // with leading/trailing whitespace that matter for signing). We
    // accept the value verbatim; the server rejects pure-whitespace.
    if (secretKey.length === 0 || secretKey.trim().length === 0) {
      setFormError(t('payment_providers.create.error.secret_required'));
      return;
    }
    if (submitTrim.length === 0) {
      setFormError(t('payment_providers.create.error.submit_url_required'));
      return;
    }
    if (queryTrim.length === 0) {
      setFormError(t('payment_providers.create.error.query_url_required'));
      return;
    }
    if (provider === 'zpay' && mapiTrim.length === 0) {
      setFormError(t('payment_providers.create.error.mapi_url_required_zpay'));
      return;
    }

    setDraft({
      provider,
      pid: pidTrim,
      secretKey,
      submitUrl: submitTrim,
      mapiUrl: mapiTrim,
      queryUrl: queryTrim,
      note: note.trim(),
      makeActive,
    });
  }

  function handleModalClose(): void {
    setDraft(null);
  }

  // When the user picks a different provider from the select, re-seed
  // the three URL fields with that provider's canonical defaults —
  // unless they've typed something other than the previous default
  // (then leave their edit alone).
  function handleProviderChange(next: PaymentProviderValue): void {
    setProvider(next);
    if (submitUrl === defaultSubmitUrl(provider)) {
      setSubmitUrl(defaultSubmitUrl(next));
    }
    if (mapiUrl === defaultMapiUrl(provider)) {
      setMapiUrl(defaultMapiUrl(next));
    }
    if (queryUrl === defaultQueryUrl(provider)) {
      setQueryUrl(defaultQueryUrl(next));
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="payment-providers-create-title"
      testId="payment-providers-create-modal"
      closeOnBackdrop={false}
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4 p-6">
        <header className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="material-symbols-outlined text-[24px] text-primary"
          >
            add_card
          </span>
          <h3
            id="payment-providers-create-title"
            className="text-base font-semibold text-on-surface"
          >
            {hasPriorActive
              ? t('payment_providers.create.modal.title.rotate', { provider })
              : t('payment_providers.create.modal.title.new', { provider })}
          </h3>
        </header>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('payment_providers.create.provider')}
            </span>
            <select
              value={provider}
              onChange={(e) => {
                // Options are rendered from PAYMENT_PROVIDER_VALUES, so
                // the value is always in range under normal UI use. Guard
                // anyway (no `as` escape hatches) — a browser extension
                // or e2e test injecting an invalid option would otherwise
                // silently widen `provider` to `string` and corrupt the
                // URL-defaults lookup below.
                const next = e.currentTarget.value;
                if (isPaymentProviderValue(next)) {
                  handleProviderChange(next);
                }
              }}
              data-testid="payment-providers-create-provider"
              disabled={prefillProvider !== null}
              className={INPUT_CLASS_ON_HIGHEST}
            >
              {PAYMENT_PROVIDER_VALUES.map((v) => (
                <option key={v} value={v} className="bg-surface-container-lowest">
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('payment_providers.create.pid')}
            </span>
            <input
              type="text"
              value={pid}
              onChange={(e) => setPid(e.currentTarget.value)}
              autoComplete="off"
              data-testid="payment-providers-create-pid"
              className={INPUT_CLASS_ON_HIGHEST}
            />
          </label>
        </div>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('payment_providers.create.secret')}
          </span>
          <input
            type="password"
            value={secretKey}
            onChange={(e) => setSecretKey(e.currentTarget.value)}
            autoComplete="new-password"
            data-testid="payment-providers-create-key"
            className={INPUT_CLASS_ON_HIGHEST}
          />
          <span className="text-[11px] text-outline">
            {t('payment_providers.create.secret.hint')}
          </span>
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('payment_providers.create.submit_url')}
          </span>
          <input
            type="url"
            value={submitUrl}
            onChange={(e) => setSubmitUrl(e.currentTarget.value)}
            autoComplete="off"
            data-testid="payment-providers-create-submit-url"
            className={INPUT_CLASS_ON_HIGHEST}
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('payment_providers.create.mapi_url')}
            {provider !== 'zpay' ? (
              <span className="text-outline">
                {' '}
                {t('payment_providers.create.mapi_url.optional_suffix')}
              </span>
            ) : null}
          </span>
          <input
            type="url"
            value={mapiUrl}
            onChange={(e) => setMapiUrl(e.currentTarget.value)}
            autoComplete="off"
            data-testid="payment-providers-create-mapi-url"
            className={INPUT_CLASS_ON_HIGHEST}
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('payment_providers.create.query_url')}
          </span>
          <input
            type="url"
            value={queryUrl}
            onChange={(e) => setQueryUrl(e.currentTarget.value)}
            autoComplete="off"
            data-testid="payment-providers-create-query-url"
            className={INPUT_CLASS_ON_HIGHEST}
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('payment_providers.create.note')}
          </span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            autoComplete="off"
            data-testid="payment-providers-create-note"
            className={INPUT_CLASS_ON_HIGHEST}
          />
        </label>

        <label className="flex items-start gap-3 rounded-lg bg-surface-container p-3">
          <input
            type="checkbox"
            checked={makeActive}
            onChange={(e) => setMakeActive(e.currentTarget.checked)}
            data-testid="payment-providers-create-make-active"
            className="mt-0.5 h-4 w-4 rounded border-outline-variant bg-surface-container-lowest text-primary-container focus:ring-primary focus:ring-offset-0"
          />
          <span className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-on-surface">
              {t('payment_providers.create.active_checkbox', { provider })}
            </span>
            <span
              className={[
                'text-[11px]',
                hasPriorActive && makeActive ? 'text-tertiary' : 'text-outline',
              ].join(' ')}
            >
              {hasPriorActive && makeActive
                ? t('payment_providers.create.active_hint.rotation')
                : hasPriorActive
                  ? t('payment_providers.create.active_hint.retired')
                  : t('payment_providers.create.active_hint.first')}
            </span>
          </span>
        </label>

        {formError !== null ? (
          <p
            role="alert"
            className="text-xs font-medium text-error"
            data-testid="payment-providers-create-error"
          >
            {formError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="payment-providers-create-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            data-testid="payment-providers-create-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)]"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
              save
            </span>
            <span>
              {hasPriorActive && makeActive
                ? t('payment_providers.create.submit.continue')
                : t('payment_providers.create.submit.save')}
            </span>
          </button>
        </div>
      </form>

      {draft !== null ? (
        <ConfirmModal
          draft={draft}
          hasPriorActive={hasPriorActive}
          onClose={(success) => {
            handleModalClose();
            if (success) onClose();
          }}
        />
      ) : null}
    </Modal>
  );
}

// ─── Type-to-confirm modal ───────────────────────────────────────────────

interface ConfirmModalProps {
  readonly draft: DraftRow;
  readonly hasPriorActive: boolean;
  readonly onClose: (success: boolean) => void;
}

function ConfirmModal({ draft, hasPriorActive, onClose }: ConfirmModalProps): JSX.Element {
  const t = useT();
  const create = useCreatePaymentProvider();
  const toast = useToast();

  // Type-to-confirm is only required when make_active=true AND a prior
  // active row exists (i.e. this write will RETIRE another row). All
  // other writes go through with a single submit click.
  const requiresTypeConfirm = draft.makeActive && hasPriorActive;
  const [typed, setTyped] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const typedMatches = !requiresTypeConfirm || typed.trim() === draft.provider;

  useEffect(() => {
    setServerError(null);
  }, [typed]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!typedMatches) {
      setServerError(
        t('payment_providers.confirm.error.type_mismatch', { provider: draft.provider }),
      );
      return;
    }
    try {
      const body: CreatePaymentProviderRequest = {
        provider: draft.provider,
        pid: draft.pid,
        secret_key: draft.secretKey,
        submit_url: draft.submitUrl,
        mapi_url: draft.mapiUrl.length > 0 ? draft.mapiUrl : null,
        query_url: draft.queryUrl,
        make_active: draft.makeActive,
        note: draft.note.length > 0 ? draft.note : undefined,
      };
      const res = await create.mutateAsync(body);
      toast.success(
        res.retired !== null
          ? t('payment_providers.toast.rotated', {
              provider: draft.provider,
              retiredId: res.retired.id,
            })
          : t('payment_providers.toast.saved', {
              provider: draft.provider,
              status: res.created.is_active
                ? t('payment_providers.toast.status.active')
                : t('payment_providers.toast.status.retired'),
            }),
      );
      onClose(true);
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setServerError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => onClose(false)}
      titleId="payment-providers-confirm-title"
      testId="payment-providers-confirm-modal"
      closeOnBackdrop={false}
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4 p-6">
        <header className="flex flex-col gap-2">
          <h3
            id="payment-providers-confirm-title"
            className="text-base font-semibold text-on-surface"
          >
            {requiresTypeConfirm
              ? t('payment_providers.confirm.title.rotation', { provider: draft.provider })
              : t('payment_providers.confirm.title.save', { provider: draft.provider })}
          </h3>
          <p className="text-sm text-on-surface-variant">
            {requiresTypeConfirm
              ? t('payment_providers.confirm.body.rotation')
              : t('payment_providers.confirm.body.save')}
          </p>
        </header>

        <div className="flex flex-col gap-2 rounded-lg bg-surface-container-lowest p-4 font-mono text-xs">
          <span>
            <span className="text-outline">provider:</span>{' '}
            <span className="text-primary">{draft.provider}</span>
          </span>
          <span>
            <span className="text-outline">pid:</span>{' '}
            <span className="text-on-surface">{draft.pid}</span>
          </span>
          <span>
            <span className="text-outline">key:</span>{' '}
            <span
              className="text-on-surface"
              data-testid="payment-providers-confirm-key-preview"
            >
              {maskKeyPreview(draft.secretKey)}
            </span>
          </span>
          <span>
            <span className="text-outline">make_active:</span>{' '}
            <span className={draft.makeActive ? 'text-primary' : 'text-outline'}>
              {draft.makeActive ? 'true' : 'false'}
            </span>
          </span>
        </div>

        {requiresTypeConfirm ? (
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('payment_providers.confirm.type_prefix')}
              <code className="font-mono text-primary">{draft.provider}</code>
              {t('payment_providers.confirm.type_suffix')}
            </span>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.currentTarget.value)}
              autoComplete="off"
              data-testid="payment-providers-confirm-type"
              className={INPUT_CLASS_MONO_ON_HIGHEST}
            />
          </label>
        ) : null}

        {serverError !== null ? (
          <p
            role="alert"
            className="text-xs font-medium text-error"
            data-testid="payment-providers-confirm-error"
          >
            {serverError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => onClose(false)}
            data-testid="payment-providers-confirm-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
          >
            {t('payment_providers.confirm.back')}
          </button>
          <button
            type="submit"
            disabled={!typedMatches || create.isPending}
            data-testid="payment-providers-confirm-submit"
            className={[
              'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] transition-all disabled:opacity-60',
              requiresTypeConfirm
                ? 'bg-error-container text-on-error-container'
                : 'bg-primary-container text-on-primary hover:shadow-[0_0_16px_rgba(173,198,255,0.2)]',
            ].join(' ')}
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
              {requiresTypeConfirm ? 'swap_horiz' : 'save'}
            </span>
            <span>
              {create.isPending
                ? t('payment_providers.confirm.submit.saving')
                : requiresTypeConfirm
                  ? t('payment_providers.confirm.submit.rotate')
                  : t('payment_providers.confirm.submit.save')}
            </span>
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function formatIso(iso: string): string {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return iso;
  return new Date(parsed).toISOString().replace('T', ' ').slice(0, 19) + 'Z';
}

/**
 * Render a key preview for the ConfirmModal diff pane. For keys longer
 * than 8 chars, show first 4 + bullets + last 4 (same mask language the
 * server uses in the list view). For keys ≤ 8 chars the head/tail
 * slices would OVERLAP and expose plaintext (e.g. a 4-char key would
 * render as `abcdabcd`); in that case render all bullets so the masked
 * affordance stays honest at every length. Mirrors the `maskSecretKey`
 * logic in `apps/platform-api/src/db/payment-provider-configs.ts`.
 */
export function maskKeyPreview(key: string): string {
  if (key.length <= 8) {
    return '•'.repeat(Math.max(1, key.length));
  }
  return `${key.slice(0, 4)}${'•'.repeat(key.length - 8)}${key.slice(-4)}`;
}

/**
 * Canonical URL defaults per provider — used to pre-populate the create
 * form. Operators can override if an aggregator routes them at regional
 * endpoints, but the defaults cover the common case.
 *
 * Modeled as `Record<PaymentProviderValue, string>` (not a switch) so
 * adding a new value to `PAYMENT_PROVIDER_VALUES` that isn't also added
 * to these tables is a compile error — no silent `undefined` returns
 * from exhaustive-looking but incomplete switches.
 *
 * Empty string means "no canonical default; the operator must type one"
 * — Stripe/WeChat/Alipay MAPI doesn't exist (different mode), and
 * WeChat/Alipay official APIs have tenant-specific endpoints.
 */
const DEFAULT_SUBMIT_URL: Record<PaymentProviderValue, string> = {
  zpay: 'https://zpayz.cn/submit.php',
  stripe: 'https://api.stripe.com/v1/checkout/sessions',
  wechat: '',
  alipay: '',
};

const DEFAULT_MAPI_URL: Record<PaymentProviderValue, string> = {
  zpay: 'https://zpayz.cn/mapi.php',
  stripe: '',
  wechat: '',
  alipay: '',
};

const DEFAULT_QUERY_URL: Record<PaymentProviderValue, string> = {
  zpay: 'https://zpayz.cn/api.php',
  stripe: 'https://api.stripe.com/v1/checkout/sessions',
  wechat: '',
  alipay: '',
};

function defaultSubmitUrl(provider: PaymentProviderValue): string {
  return DEFAULT_SUBMIT_URL[provider];
}

function defaultMapiUrl(provider: PaymentProviderValue): string {
  return DEFAULT_MAPI_URL[provider];
}

function defaultQueryUrl(provider: PaymentProviderValue): string {
  return DEFAULT_QUERY_URL[provider];
}
